import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Calendar, Plane, Activity, Settings,
  CheckCircle2, Database, Loader2,
  FileText, X, RefreshCw,
} from 'lucide-react';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { projectsService, Project } from '../../services/projects';
import StatusBadge from '../../components/common/StatusBadge';
import DataModal from '../../components/projects/DataModal';
import { ErrorBoundary } from '../../components/common/ErrorBoundary';
import ProjectConfigurationModal, { FleetScore, INITIAL_FLEET_SCORES } from './ProjectConfigurationModal';
import { ProjectProvider } from './ProjectContext';
import { useShellStatus } from '../../context/ShellStatusContext';

/**
 * Shell for every /projects/:id route.
 *
 * Owns what is cross-cutting -- loading the project, Celery polling, the header,
 * the sub-route nav, the configuration/data/APCAP modals and the status banners --
 * and renders the active sub-route through the Outlet. This is what replaced the
 * five-tab monolith: each tab is now a route with its own URL, which is what lets
 * the shell rail point at a module instead of dumping every item on one page.
 */

const ProjectLayout: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { language } = useLanguage();

  const isDark = theme === 'dark';
  const bgPage = isDark ? 'bg-[var(--bg)]' : 'bg-[#f4f6f8]';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)] shadow-sm';

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);

  const [showSettings, setShowSettings] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [aipReparsing, setAipReparsing] = useState(false);
  const aipFileInputRef = React.useRef<HTMLInputElement>(null);
  const [fleetScores, setFleetScores] = useState<FleetScore[]>(INITIAL_FLEET_SCORES);

  const [showApcapModal, setShowApcapModal] = useState(false);
  const [apcapLang, setApcapLang] = useState<'es' | 'en'>('es');
  const [apcapGenerating, setApcapGenerating] = useState(false);
  const [apcapError, setApcapError] = useState<string | null>(null);

  // Client-side ADOSA recalculation. Owned here rather than by the ADOSA route
  // because the KPI figures on other routes read the recalculated values too;
  // one owner is what stops the two from drifting apart.
  const [adosaOverride, setAdosaOverride] = useState<Record<string, any> | null>(null);

  const loadProject = async (silent = false) => {
    if (!id) return;
    try {
      if (!silent) setLoading(true);
      const data = await projectsService.getOne(id);
      if (data) setProject(data);
      else setErrorMsg('Sin datos.');
    } catch (error) {
      if (!silent) setErrorMsg('Error de conexión.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { loadProject(); }, [id]);

  // Publish the active analysis's data-confidence to the shell (topbar chip).
  // Cleared on unmount so the chip disappears when leaving the project.
  const { setStatus } = useShellStatus();
  useEffect(() => {
    const prov = (project?.analysis_results as any)?.provenance;
    if (prov?.data_confidence) {
      setStatus({
        confidence: prov.data_confidence,
        methodologyVersion: prov.methodology_version,
        flagsCount: Array.isArray(prov.quality_flags) ? prov.quality_flags.length : 0,
        defaultsCount: Array.isArray(prov.defaults_used) ? prov.defaults_used.length : 0,
      });
    } else {
      setStatus(null);
    }
    return () => setStatus(null);
  }, [project, setStatus]);

  // Refresh while the analysis runs, and poll the task for granular progress.
  useEffect(() => {
    let interval: any = null;
    if (project?.analysis_status === 'PROCESSING') {
      interval = setInterval(async () => {
        loadProject(true);
        if (currentTaskId) {
          try {
            const status = await projectsService.getTaskStatus(currentTaskId);
            if (status.progress) setAnalysisProgress(status.progress);
            if (status.status === 'SUCCESS') {
              setAnalysisProgress(100);
              setCurrentTaskId(null);
            }
          } catch (e) { console.warn('Task polling error', e); }
        }
      }, 3000);
    } else {
      setAnalysisProgress(0);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [project?.analysis_status, id, currentTaskId]);

  const handleGenerateApcap = async () => {
    if (!id) return;
    setApcapGenerating(true);
    setApcapError(null);
    try {
      const blob = await projectsService.generateApcap(id, apcapLang);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const airport = project?.icao_code || project?.iata_code || 'APCAP';
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `APCAP_${airport}_${dateStr}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      setShowApcapModal(false);
    } catch (err: any) {
      setApcapError(err?.response?.data?.detail || err?.message || 'Error generating APCAP');
    } finally {
      setApcapGenerating(false);
    }
  };

  const handleAipReparse = async (file: File) => {
    if (!project) return;
    setAipReparsing(true);
    try {
      const result = await projectsService.reparseAip(project.id, file);
      const summary = result.summary;
      alert(
        language === 'es'
          ? `AIP AD2 actualizado correctamente.\n\nStands: ${summary.stands}\nRunways: ${summary.runways}\nTaxiways: ${summary.taxiways}\nConfianza: ${Math.round(summary.confidence * 100)}%\nSecciones: ${summary.parsed_sections.join(', ')}\n\nRe-ejecuta el análisis para aplicar los nuevos datos.`
          : `AIP AD2 updated successfully.\n\nStands: ${summary.stands}\nRunways: ${summary.runways}\nTaxiways: ${summary.taxiways}\nConfidence: ${Math.round(summary.confidence * 100)}%\nSections: ${summary.parsed_sections.join(', ')}\n\nRe-run the analysis to apply the new data.`
      );
      loadProject();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Unknown error';
      alert(`Error: ${detail}`);
    } finally {
      setAipReparsing(false);
    }
  };

  const handleScoreChange = (index: number, field: keyof FleetScore, value: string) => {
    const newScores = [...fleetScores];
    // @ts-ignore
    newScores[index][field] = parseInt(value) || 0;
    setFleetScores(newScores);
  };

  const stopAnalysis = async () => {
    if (!project) return;
    if (!window.confirm(language === 'es' ? 'Detener el analisis en curso?' : 'Stop the running analysis?')) return;
    try {
      await projectsService.stopAnalysis(project.id);
      await loadProject(true);
    } catch (e) { console.warn('stop failed', e); }
  };

  const reanalyze = async () => {
    if (!project) return;
    try {
      const res = await projectsService.reanalyze(project.id);
      if (res?.task_id) setCurrentTaskId(res.task_id);
      await loadProject(true);
    } catch (e) { console.warn('reanalyze failed', e); }
  };

  // Airport centre: analysis_results first, then the AIP AD2 DMS coordinates.
  const airportCenter = useMemo(() => {
    const fromAnalysis = project?.analysis_results?.airport_center;
    if (fromAnalysis && (fromAnalysis.lat || fromAnalysis.lat === 0) && (fromAnalysis.lon || fromAnalysis.lon === 0)) {
      return fromAnalysis;
    }
    const aipData = project?.sme_parameters?.aip_data;
    if (aipData?.latitude && aipData?.longitude) {
      const parseDms = (dms: string): number | null => {
        const m = dms.match(/(\d+)d(\d+)m([\d.]+)s([NSEW])/);
        if (!m) return null;
        let dec = parseFloat(m[1]) + parseFloat(m[2]) / 60 + parseFloat(m[3]) / 3600;
        if (m[4] === 'S' || m[4] === 'W') dec = -dec;
        return dec;
      };
      const lat = parseDms(aipData.latitude);
      const lon = parseDms(aipData.longitude);
      if (lat !== null && lon !== null) return { lat, lon };
    }
    return null;
  }, [project]);

  if (loading) return <div className={`min-h-screen flex items-center justify-center ${bgPage}`}><Loader2 className="animate-spin w-12 h-12 text-[#F26430]" /></div>;
  if (errorMsg || !project) return <div className={`min-h-screen flex items-center justify-center ${bgPage} ${textMain}`}>{errorMsg}</div>;

  return (
    <ProjectProvider
      value={{
        project,
        reload: loadProject,
        currentTaskId,
        setCurrentTaskId,
        analysisProgress,
        adosaOverride,
        setAdosaOverride,
        airportCenter,
      }}
    >
      {/* Modals live outside the layout flow to keep them clear of stacking contexts */}
      <ProjectConfigurationModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        project={project}
        isDark={isDark}
        onUpdate={() => loadProject(true)}
        language={language}
        fleetScores={fleetScores}
        handleScoreChange={handleScoreChange}
      />
      <DataModal
        isOpen={showDataModal}
        onClose={() => { setShowDataModal(false); loadProject(true); }}
        isDark={isDark}
        projectId={project.id}
        onUploadSuccess={(taskId?: string) => {
          // Deliberately no reload here: it re-renders the parent and resets the
          // modal's own state mid-upload.
          if (taskId) setCurrentTaskId(taskId);
        }}
      />

      <div className={`min-h-screen ${bgPage}`}>
        <div className={`px-8 py-6 border-b sticky top-16 z-20 backdrop-blur-md bg-opacity-90 ${isDark ? 'bg-[var(--background)]/90 border-[#2a2a2a]' : 'bg-white/90 border-[var(--border)]'}`}>
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-4">
                <button onClick={() => navigate('/analysis')} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-[var(--surface-inset)] text-white' : 'hover:bg-gray-100 text-gray-700'}`}><ArrowLeft size={24} /></button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className={`text-3xl font-extrabold tracking-tight ${textMain}`}>{project.name}</h1>
                    <StatusBadge status={project.analysis_status as any} />
                  </div>
                  <div className={`flex items-center gap-4 mt-1 text-xs font-mono ${textSub}`}>
                    <span className="flex items-center gap-1"><Plane size={12} /> {project.iata_code} / {project.icao_code}</span>
                    <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(project.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors border ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)] text-white hover:bg-[var(--surface-inset)]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  <Settings size={16} /> <span>{language === 'es' ? 'Parámetros de Cálculo' : 'Calculation Parameters'}</span>
                </button>
                <button
                  onClick={() => setShowApcapModal(true)}
                  disabled={project.analysis_status !== 'COMPLETED'}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold shadow-lg transition-all active:scale-95 ${
                    project.analysis_status === 'COMPLETED'
                      ? 'bg-gradient-to-r from-[#F26430] to-[#e8501e] hover:from-[#e8501e] hover:to-[#d94518] text-white cursor-pointer'
                      : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  }`}
                >
                  <FileText size={16} /> <span>{language === 'es' ? 'Generar APCAP' : 'Generate APCAP'}</span>
                </button>
              </div>
            </div>

            {/* APCAP Language Selection Modal */}
            {showApcapModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <div className={`w-full max-w-md rounded-2xl p-6 shadow-2xl border ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-5">
                    <h3 className={`text-lg font-bold ${textMain}`}>
                      {language === 'es' ? 'Generar Plan de Capacidad (APCAP)' : 'Generate Capacity Plan (APCAP)'}
                    </h3>
                    <button onClick={() => setShowApcapModal(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                      <X size={18} />
                    </button>
                  </div>

                  <p className={`text-sm mb-5 ${textSub}`}>
                    {language === 'es'
                      ? 'Se generará un documento Word (.docx) con el análisis completo de capacidad, demanda y dotación de personal conforme a la metodología DACAMS/ICAO Doc 9476.'
                      : 'A Word document (.docx) will be generated with the complete capacity, demand and staffing analysis following the DACAMS/ICAO Doc 9476 methodology.'}
                  </p>

                  <div className="mb-5">
                    <label className={`block text-sm font-semibold mb-2 ${textMain}`}>
                      {language === 'es' ? 'Idioma del documento' : 'Document language'}
                    </label>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setApcapLang('es')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border-2 transition-all ${
                          apcapLang === 'es'
                            ? 'border-[#F26430] bg-[#F26430]/10 text-[#F26430]'
                            : isDark ? 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        Español
                      </button>
                      <button
                        onClick={() => setApcapLang('en')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border-2 transition-all ${
                          apcapLang === 'en'
                            ? 'border-[#F26430] bg-[#F26430]/10 text-[#F26430]'
                            : isDark ? 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        English
                      </button>
                    </div>
                  </div>

                  {apcapError && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                      {apcapError}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowApcapModal(false)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${isDark ? 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                    >
                      {language === 'es' ? 'Cancelar' : 'Cancel'}
                    </button>
                    <button
                      onClick={handleGenerateApcap}
                      disabled={apcapGenerating}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-[#F26430] to-[#e8501e] hover:from-[#e8501e] hover:to-[#d94518] text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {apcapGenerating ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          {language === 'es' ? 'Generando APCAP...' : 'Generating APCAP...'}
                        </>
                      ) : (
                        <>
                          <FileText size={16} />
                          {language === 'es' ? 'Generar Documento' : 'Generate Document'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Module navigation now lives in the shell rail (single source of
                truth). The old in-project tab bar was removed to stop duplicating
                it here. */}
          </div>
        </div>

        <div className="pt-14 p-8 max-w-7xl mx-auto">
          <div className="space-y-6">
              {project.analysis_status === 'PROCESSING' && (
                <div className={`p-6 rounded-xl border border-l-4 border-l-blue-500 flex flex-col gap-4 animate-fade-in ${isDark ? 'bg-blue-900/10 border-[var(--border)]' : 'bg-blue-50 border-blue-100'}`}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-500/20 rounded-full text-blue-500"><Loader2 className="animate-spin" size={24} /></div>
                      <div>
                        <h3 className={`font-bold ${textMain}`}>Analizando Datos...</h3>
                        <p className={`text-sm ${textSub}`}>El motor matemático está procesando los vuelos y calculando capacidades.</p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <span className="text-lg font-black text-blue-500">{analysisProgress}%</span>
                      <button
                        onClick={async () => {
                          if (!window.confirm("¿Seguros que deseas detener el análisis?")) return;
                          try {
                            await projectsService.stopAnalysis(project.id);
                            setAnalysisProgress(0);
                            loadProject();
                          } catch (e: any) {
                            console.error(e);
                            const detail = e?.response?.data?.detail || e?.message || "Error desconocido";
                            alert(`Error al detener el análisis:\n${detail}`);
                          }
                        }}
                        className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded shadow"
                      >
                        ⏹ Detener
                      </button>
                    </div>
                  </div>

                  {/* PROGRESS BAR */}
                  <div className="w-full h-2 bg-gray-200 dark:bg-[var(--surface-inset)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-1000 ease-out shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                      style={{ width: `${analysisProgress || 5}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs font-bold text-[var(--text-secondary)] uppercase tracking-widest">
                    <span>{language === 'es' ? 'Demanda' : 'Demand'}</span>
                    <span>{language === 'es' ? 'Infraestructura' : 'Infrastructure'}</span>
                    <span>AP-CAM Logic</span>
                    <span>ADOSA Staffing</span>
                  </div>
                </div>
              )}

              {(!project.has_file || project.analysis_status === 'DRAFT' || project.analysis_status === 'PENDING') && project.analysis_status !== 'PROCESSING' && (
                <div className={`p-6 rounded-xl border border-l-4 border-l-[#F26430] flex flex-col sm:flex-row items-center justify-between gap-4 ${isDark ? 'bg-orange-900/10 border-[var(--border)]' : 'bg-orange-50 border-orange-100'}`}>
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#F26430]/20 rounded-full text-[#F26430]"><Database size={24} /></div>
                    <div>
                      <h3 className={`font-bold ${textMain}`}>
                        {language === 'es' ? 'Carga de Datos Pendiente' : 'Data Upload Pending'}
                      </h3>
                      <p className={`text-sm ${textSub}`}>
                        {language === 'es'
                          ? 'Sube el archivo de datos para ejecutar el pipeline de análisis completo.'
                          : 'Upload data file to run the complete analysis pipeline.'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDataModal(true)}
                    className="px-4 py-2 rounded-lg text-sm font-bold shadow bg-gradient-to-r from-[#F26430] to-[#E85D04] text-white hover:opacity-90 transition-all active:scale-[0.98]"
                  >
                    {language === 'es' ? 'Subir Archivos' : 'Upload Files'}
                  </button>
                </div>
              )}

              {/* Opción de re-subir cuando hay archivo cargado (cualquier estado excepto PROCESSING) */}
              {project.has_file && project.analysis_status !== 'PROCESSING' && (
                <div className={`p-4 rounded-xl border flex flex-col sm:flex-row items-center justify-between gap-4 ${project.analysis_status === 'COMPLETED'
                  ? (isDark ? 'bg-emerald-900/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200')
                  : (isDark ? 'bg-orange-900/10 border-orange-500/30' : 'bg-orange-50 border-orange-200')
                  }`}>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${project.analysis_status === 'COMPLETED'
                      ? 'bg-emerald-500/20 text-emerald-500'
                      : 'bg-orange-500/20 text-orange-500'
                      }`}>
                      {project.analysis_status === 'COMPLETED' ? <CheckCircle2 size={20} /> : <Activity size={20} />}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${textMain}`}>
                        {project.analysis_status === 'COMPLETED'
                          ? (language === 'es' ? 'Análisis Completado' : 'Analysis Completed')
                          : (language === 'es' ? 'Archivo Cargado' : 'File Loaded')}
                      </p>
                      <p className={`text-xs ${textSub}`}>
                        {project.analysis_status === 'FAILED' && (language === 'es' ? 'Error en análisis - ' : 'Analysis error - ')}
                        {language === 'es' ? 'Última actualización' : 'Last updated'}: {project.updated_at ? new Date(project.updated_at).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          console.log("Re-ejecutando análisis con configuración actual...");
                          const res = await projectsService.reanalyze(project.id);
                          if (res.task_id) setCurrentTaskId(res.task_id);
                          loadProject();
                        } catch (e: any) {
                          console.error("Error re-analizando:", e);
                          const errorMsg = e?.response?.data?.detail || e?.message || "Error desconocido";
                          alert(`Error al re-ejecutar análisis:\n${errorMsg}`);
                        }
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                    >
                      <Activity size={14} />
                      {language === 'es' ? 'Re-ejecutar Análisis' : 'Re-run Analysis'}
                    </button>
                    <button
                      onClick={() => setShowDataModal(true)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${isDark ? 'border-[var(--border)] hover:bg-[var(--surface-inset)] text-gray-300' : 'border-gray-300 hover:bg-gray-100 text-gray-600'}`}
                    >
                      <RefreshCw size={14} />
                      {language === 'es' ? 'Nuevo Archivo' : 'New File'}
                    </button>
                    <button
                      onClick={() => aipFileInputRef.current?.click()}
                      disabled={aipReparsing}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${isDark ? 'border-purple-500/40 hover:bg-purple-900/20 text-purple-300' : 'border-purple-300 hover:bg-purple-50 text-purple-600'} ${aipReparsing ? 'opacity-50 cursor-wait' : ''}`}
                    >
                      {aipReparsing ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                      {aipReparsing
                        ? (language === 'es' ? 'Procesando AD2...' : 'Processing AD2...')
                        : (language === 'es' ? 'Actualizar AIP AD2' : 'Update AIP AD2')}
                    </button>
                    <input
                      ref={aipFileInputRef}
                      type="file"
                      accept=".pdf,.html,.htm,.txt,.xhtml"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleAipReparse(file);
                        e.target.value = '';
                      }}
                    />
                  </div>
                </div>
              )}

            <ErrorBoundary showDetails={true}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </ProjectProvider>
  );
};

export default ProjectLayout;
